import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ServicePageTemplate } from "@/components/ServicePageTemplate";
import { getService } from "@/data/services";
import techSupportFaqsData from "@/data/tech-support-faqs.json";
import voipFaqsData from "@/data/voip-faqs.json";
import websiteDesignFaqsData from "@/data/website-design-faqs.json";
import softwareIntegrationFaqsData from "@/data/software-integration-faqs.json";
import seniorsFaqsData from "@/data/tech-support-for-seniors-faqs.json";
import adaFaqsData from "@/data/ada-compliant-website-faqs.json";
import cyberFaqsData from "@/data/cyber-security-faqs.json";
import { SITE_URL, canonical, seoCanonicalLink, seoOgUrl, withSiteSuffix } from "@/lib/seo";

const FAQS_BY_SLUG: Record<string, { q: string; a: string }[]> = {
  "tech-support": techSupportFaqsData.faqs,
  voip: voipFaqsData.faqs,
  "website-design": websiteDesignFaqsData.faqs,
  "software-integration": softwareIntegrationFaqsData.faqs,
  "tech-support-for-seniors": seniorsFaqsData.faqs,
  "ada-compliant-website": adaFaqsData.faqs,
  "cyber-security": cyberFaqsData.faqs,
};

const SLUG_REDIRECTS: Record<string, string> = {
  "website-design-montclair": "website-design",
};

export const Route = createFileRoute("/services_/$slug")({
  loader: ({ params }) => {
    const target = SLUG_REDIRECTS[params.slug];
    if (target) {
      throw redirect({ to: "/services/$slug", params: { slug: target }, statusCode: 301 });
    }
    const service = getService(params.slug);
    if (!service) throw notFound();
    return { slug: service.slug };
  },
  head: ({ params, loaderData }) => {
    const s = loaderData ? getService(loaderData.slug) : undefined;
    if (!s) return { meta: [{ title: "Service not found — SimpleTech" }] };
    const titleOverrides: Record<string, string> = {
      voip: "Business VoIP Phone Systems — Montclair & Inland Empire",
      "website-design": "Professional Website Design for Small Businesses | SimpleTech",
      "software-integration": "Software Integration & Workflow Automation | SimpleTech",
      "tech-support-for-seniors": "Patient Tech Support for Seniors | In-Home & Remote Help",
      "ada-compliant-website": "ADA Website Compliance & WCAG 2.1 AA Audits | SimpleTech",
      "cyber-security": "Cybersecurity Services for Small & Medium Businesses | SimpleTech",
    };
    const descriptionOverrides: Record<string, string> = {
      voip: "Secure, cost-effective business VoIP for Inland Empire offices. Free 30-minute consultation and full setup handled by SimpleTech.",
    };
    const pageDescription = descriptionOverrides[s.slug] ?? s.seoDescription;
    const pageTitle = withSiteSuffix(titleOverrides[s.slug] ?? `${s.shortTitle} — SimpleTech`);
    return {
      meta: [
        { title: pageTitle },
        { name: "description", content: pageDescription },
        { property: "og:title", content: pageTitle },
        { property: "og:description", content: pageDescription },
        seoOgUrl(`/services/${params.slug}`),
        { property: "og:type", content: "website" },
      ],
      links: [seoCanonicalLink(`/services/${params.slug}`)],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: s.shortTitle,
            description: s.seoDescription,
            provider: {
              "@type": "LocalBusiness",
              name: "SimpleTech",
              telephone: "+1-909-255-9112",
              address: {
                "@type": "PostalAddress",
                streetAddress: "8880 N Benson Ave #121",
                addressLocality: "Montclair",
                addressRegion: "CA",
                postalCode: "91763",
                addressCountry: "US",
              },
            },
            areaServed: "Montclair, CA",
          }),
        },
        ...(FAQS_BY_SLUG[s.slug]
          ? [{
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: FAQS_BY_SLUG[s.slug].map(({ q, a }) => ({
                  "@type": "Question",
                  name: q,
                  acceptedAnswer: { "@type": "Answer", text: a },
                })),
              }),
            }]
          : []),
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader active="services" />
      <div className="max-w-3xl mx-auto px-8 py-32 text-center">
        <h1 className="text-4xl font-extrabold text-slate-900 mb-4">Service not found</h1>
        <p className="text-slate-600 mb-8">We couldn't find that service. Browse all of our services below.</p>
        <Link to="/services" className="inline-block bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors">
          ALL SERVICES
        </Link>
      </div>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="min-h-screen grid place-items-center px-6">
      <p className="text-slate-600">{error.message}</p>
    </div>
  ),
  component: ServiceDetail,
});

function ServiceDetail() {
  const { slug } = Route.useLoaderData();
  const service = getService(slug)!;
  return <ServicePageTemplate service={service} />;
}
